import services from '@/services'
import { emitAppSession, getExternalSessionKey } from '@/services/iframe'

import { comparators } from '@/helpers'

import * as types from './mutations-types'

import store from '@/store'

const getCustomerName = customer => {
  let name = customer.name

  if (customer.apps) {
    const chatApp = customer.apps.find(a => a.productName === 'Chat')

    if (chatApp && chatApp.fields && chatApp.fields.name) {
      name = chatApp.fields.name
    }
  }

  return name
}

export const upsertUser = async (context, userData) => {
  if (userData.guest) {
    context.dispatch('setUser', userData)

    const sessionKey = getExternalSessionKey('guestUser')

    if (sessionKey) {
      emitAppSession({
        [sessionKey]: JSON.stringify(userData)
      })
    }

    return
  }

  let user

  const cachedUser = await context.dispatch('getCachedUser')

  if (cachedUser) {
    user = cachedUser
  } else {
    user = await services.customers.upsertUser({
      email: userData.email,
      name: userData.name
    })
  }

  const { fluxId } = context.getters

  if (fluxId) {
    const statistics = services.flux.fluxStatistics(fluxId)

    if (user.existent) {
      statistics.contactsExistent()
    } else {
      statistics.contactsNew()
    }
  }

  user.customer.name = getCustomerName(user.customer)

  context.dispatch('setExistentContact', user.existent)

  const sessionKey = getExternalSessionKey('userId')

  if (sessionKey) {
    emitAppSession({ [sessionKey]: user.customer.id })
  }

  context.dispatch('setUser', user.customer)

  context.dispatch('setCachedUser', user)
}

export const setUser = (context, user) => {
  if (user.email) {
    const { chat } = context.getters

    if (
      chat &&
      chat.createdBy &&
      !chat.createdBy.guest &&
      String(chat.createdBy.email).toLowerCase() !==
        String(user.email).toLowerCase()
    ) {
      context.dispatch('clearSession')
    }
  }

  context.commit(types.SET_USER, user)

  services.session.setUserId(user.id)
}

export const setUserEmail = (context, email) =>
  context.commit(types.SET_USER_EMAIL, email)

export const setUserId = (context, id) => {
  context.commit(types.SET_USER_ID, id)
}

export const findCustomer = async (context, customerId) => {
  const cachedUser = await context.dispatch('getCachedUser')

  const cachedCustomer = cachedUser && cachedUser.customer

  const customer = cachedCustomer
    ? cachedCustomer
    : await services.customers.getCustomer(customerId)

  if (customer) {
    customer.name = getCustomerName(customer)

    context.dispatch('setUser', customer)

    context.dispatch('setExistentContact', true)
  }
}

export const validLogin = async context => {
  let valid = await context.dispatch('validUser')

  if (valid) {
    valid = await context.dispatch('validCustomFields')
  }

  return valid
}

export const login = async (context, form) => {
  await context.dispatch('setCustomFields', form.customFields)

  await context.dispatch('upsertUser', form.user)

  if (!context.getters.fluxId) {
    await context.dispatch('chooseFlux')
  }

  services.iframe.postMessage({
    name: 'onLogin',
    user: context.getters.user
  })

  await context.dispatch('goToConversation')
}

const getConditionField = condition => {
  let conditionField

  const { customFields } = store.getters

  if (customFields) {
    conditionField = (customFields || []).find(
      c => c.id === condition.field.name
    )
  }

  if (conditionField && condition.field.name === 'organization') {
    const { user, organization } = store.getters

    if (user || organization) {
      conditionField = {
        value:
          organization ||
          user.organizations.find(o => o.default) ||
          user.organization
      }
    }
  }

  return conditionField
}

const validateCondition = condition => {
  const stateField = getConditionField(condition)
  // verify if the value of the login form exists to compare with the value stored in condition value
  if (stateField) {
    if (condition.value instanceof Object) {
      if (condition.value instanceof Array) {
        // field value is Array
        if (condition.value.length !== stateField.value.length) {
          return false
        }

        const validValues = condition.value.every((v, index) => {
          if (v instanceof Object) {
            if (typeof v.id !== 'undefined') {
              const comparation = comparators[condition.comparator.type](
                v.id,
                stateField.value[index].id
              )

              return comparation
            }
          } else {
            const comparation = comparators[condition.comparator.type](
              v,
              stateField.value[index]
            )

            return comparation
          }
        })

        return validValues
      }
      if (typeof condition.value.id !== 'undefined') {
        // field value is Object and has id
        const comparation = comparators[condition.comparator.type](
          condition.value.id,
          stateField.value.id
        )

        return comparation
      }
    } else {
      // field value is Primitive Type(String, Number, Boolean...)
      const comparation = comparators[condition.comparator.type](
        condition.value,
        stateField.value
      )

      return comparation
    }
  }

  return false
}

const validateGroupAtributtionRule = rules => {
  const validRule = rules.find(
    r =>
      r.conditions.length === 0 ||
      (r.allConditions && r.conditions.every(c => validateCondition(c))) ||
      (!r.allConditions && r.conditions.some(c => validateCondition(c)))
  )

  return validRule
}

export const verifyOnlineAgents = async context => {
  let onlineCount = 0

  const useDistributor =
    context.getters.settings && context.getters.settings.useDistributor

  const { flux } = context.getters

  if (flux) {
    if (!flux.rules || !flux.rules.length) {
      console.log('flux.rules is empty')
    }

    if (useDistributor) {
      const agentAttributionRules = flux.rules.filter(
        r =>
          r.type === 'atributtion' &&
          r.actions.some(a => a.action.field === 'agent')
      )

      const validAgentAttributionRule = validateGroupAtributtionRule(
        agentAttributionRules
      )

      if (validAgentAttributionRule) {
        let action = validAgentAttributionRule.actions.find(
          a => a.action.field === 'agent'
        )

        const isAvailable = await services.agents.getAgentAvailabilityById(
          action.value.id
        )

        return isAvailable ? 1 : 0
      }
    }

    const groupAtributtionRules = flux.rules.filter(
      r =>
        r.type === 'atributtion' &&
        r.actions.some(a => a.action.field === 'group')
    )

    const validGroupAtributtionRule = validateGroupAtributtionRule(
      groupAtributtionRules
    )

    if (validGroupAtributtionRule) {
      const action = validGroupAtributtionRule.actions.find(
        a => a.action.field === 'group'
      )

      const actionId = action.value.id || action.value._id

      onlineCount = useDistributor
        ? await services.agents.countAvailableAgentsByGroupId(actionId)
        : await services.agents.getGroupAgentsOnlineCount(actionId)

      return onlineCount
    }
  }

  onlineCount = useDistributor
    ? await services.agents.countAvailableAgents()
    : await services.agents.getAgentsOnlineCount()

  return onlineCount
}

export const getOnlineAgents = async ({ commit, getters }) => {
  try {
    const useDistributor = getters.settings && getters.settings.useDistributor

    if (useDistributor) {
      const availableAgents = await services.agents.getAvailableAgents()

      if (availableAgents instanceof Array) {
        commit(types.SET_ONLINE_AGENTS, availableAgents)
      } else {
        throw new Error('Invalid availableAgents type, it must be a Array')
      }
    } else {
      const onlineAgents = await services.agents.getOnlineAgents()

      if (onlineAgents instanceof Array) {
        commit(types.SET_ONLINE_AGENTS, onlineAgents)
      } else {
        throw new Error('Invalid onlineAgents type, it must be a Array')
      }
    }
  } catch (ex) {
    console.error('Error at getOnlineAgents: ', ex)
  }
}

export const getCachedUser = () => {
  return services.session.getCachedUser()
}

export const setCachedUser = (_, user) => {
  if (!user) return false

  return services.session.setCachedUser(user)
}

export const clearCachedUser = () => {
  return services.session.clearCachedUser()
}
